import {
  Component,
  HostBinding,
  ChangeDetectionStrategy,
  Input,
  Output,
  OnInit,
  OnDestroy,
  EventEmitter,
  ChangeDetectorRef
} from "@angular/core";
import { FormControl } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatSlideToggleChange } from "@angular/material/slide-toggle";
import { QueueWithExt } from "../../../acdQueuesPage/acd-queues-page.component";
import { ModalMaterialComponent } from "@onsip/web/features/shared/components/modal/modal-material.component";
import { Subscription } from "rxjs";
import { take, skip, filter, map } from "rxjs/operators";
import { AdvQueueService } from "@onsip/common/services/api/resources/queue/adv-queue.service";
import { SnackbarService } from "@onsip/web/features/shared/components/snackbar/snackbar.service";
import { TranslateService } from "@ngx-translate/core";
import { AccountResourceService } from "@onsip/common/services/api/resources/package/account-resource.service";
import { ApiAdvQueueEmailReport } from "@onsip/common/services/api/resources/queue/adv-queue-email-report";
import { E2eLocators } from "@onsip/e2e/configs/locators";

@Component({
  selector: "onsip-acd-queues-enhanced-view",
  templateUrl: "./acd-queues-enhanced-view.component.html",
  styleUrls: ["../acdQueuesEnhancedQueueEdit/acd-queues-enhanced-edit.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AcdQueuesEnhancedViewComponent implements OnInit, OnDestroy {
  @HostBinding("class.onsip-grid-content")
  @HostBinding("class.onsip-admin-host-grid")
  _dontUse = true;
  @Input() queue!: QueueWithExt;
  @Input() userUnlimited!: boolean;
  @Output() editPanel = new EventEmitter<void>();
  E2eLocators = E2eLocators;

  queueReportingEnabled = false;
  callRecordingEnabled = false;
  oneColumn = false;
  twoColumn = false;
  threeColumn = false;

  enhanced = new FormControl(false, { nonNullable: true });
  showEnhancedFields = false;
  enhancedPricing!: String;

  emailReportsList: Array<ApiAdvQueueEmailReport> = [];
  emailReportsTextList: Array<string> = [];

  loginExtensions: Array<string> = [];

  private unsubscriber = new Subscription();

  constructor(
    private dialog: MatDialog,
    private queueService: AdvQueueService,
    private snackbar: SnackbarService,
    private translate: TranslateService,
    private resourceService: AccountResourceService,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.setEnhancedQueue();
    this.setQueueReporting();
    this.setCallRecording();
    this.getEnhancedPricing();
    this.getEmailReportsText();
  }

  ngOnDestroy(): void {
    this.unsubscriber.unsubscribe();
  }

  getEnhancedPricing(): void {
    this.unsubscriber.add(
      this.resourceService.state
        .pipe(
          filter(state => !state.loading),
          map(state => {
            return Object.values(state.state);
          })
        )
        .pipe(
          map(resourcesArray => {
            return resourcesArray.find(res => res.name === "SmartQueue");
          })
        )
        .subscribe(res => {
          this.enhancedPricing = res?.monthlyRecurringCharge || "undetermined";
          this.cdRef.markForCheck();
        })
    );
  }

  setQueueReporting(): void {
    this.queueReportingEnabled = this.queue.emailReports.length > 0 ? true : false;
  }

  setCallRecording(): void {
    if (
      this.queue.storageServiceName ||
      this.queue.storageServiceService ||
      (this.queue.storageServiceId && this.queue.storageServiceId !== "0")
    ) {
      this.callRecordingEnabled = true;
    } else {
      this.callRecordingEnabled = false;
    }
  }

  setEnhancedQueue(): void {
    if (this.queue.smartQueue) {
      this.enhanced.setValue(true);
      this.showEnhancedFields = true;
    } else {
      this.enhanced.setValue(false);
      this.showEnhancedFields = false;
    }
  }

  getEmailReportsText(): void {
    this.emailReportsTextList = [];
    if (this.queue.emailReports.length === 0) {
      this.emailReportsTextList.push("None");
      return;
    }
    this.queue.emailReports.map(emailRep => {
      this.emailReportsTextList.push(
        `(${emailRep.Frequency.substring(0, emailRep.Frequency.indexOf(" ")).toLowerCase()}) ${
          emailRep.ToAddress
        }`
      );
    });
  }

  getPricingText(): string {
    return (
      "Enhanced queue: " +
      (this.userUnlimited ? "Included in your plan." : `$${this.enhancedPricing}/month per agent.`)
    );
  }

  toggleEnhanced(event: MatSlideToggleChange): void {
    if (this.enhanced.value) {
      if (!this.userUnlimited) {
        const confirmModal = this.dialog.open(ModalMaterialComponent, {
          panelClass: ["mat-typography", "onsip-dialog-universal-style"],
          data: {
            title: "Upgrade to enhanced queue",
            message: `Upgrading your basic queue to an enhanced queue will cost $${
              this.enhancedPricing
            }/month per agent. Your account will be charged $${(
              +this.enhancedPricing * +this.queue.maxAgent
            ).toString()}/month + taxes & fees. Are you sure you want to upgrade?`,
            primaryBtnText: "Upgrade",
            primaryBtnFlat: true,
            secondaryBtnText: "Cancel",
            noSentenceCaseTitle: true
          }
        });
        this.unsubscriber.add(
          confirmModal
            .afterClosed()
            .pipe(take(1))
            .subscribe(res => {
              if (res && res.doPrimaryAction) {
                this.setEnhanced(true);
                this.handleEnhancedSuccessError(event);
              } else {
                this.enhanced.setValue(false);
              }
            })
        );
      } else {
        this.setEnhanced(true);
        this.handleEnhancedSuccessError(event);
      }
    } else {
      const confirmModal = this.dialog.open(ModalMaterialComponent, {
        panelClass: ["mat-typography", "onsip-dialog-universal-style"],
        data: {
          title: "Disable enhanced queue",
          message:
            "Disabling enhanced queue will remove queue reporting and call recording. Are you sure you want to disable this feature?",
          primaryBtnText: "Keep",
          primaryBtnFlat: true,
          secondaryBtnText: "Disable"
        }
      });
      this.unsubscriber.add(
        confirmModal
          .afterClosed()
          .pipe(take(1))
          .subscribe(res => {
            if (res && !res.doPrimaryAction) {
              this.setEnhanced(false);
              this.handleEnhancedSuccessError(event);
            } else {
              this.enhanced.setValue(true);
            }
          })
      );
    }
  }

  setEnhanced(willBeEnhanced: boolean): void {
    if (willBeEnhanced === this.queue.smartQueue) return;
    const params = {
      AdvQueueId: this.queue.advQueueId,
      QueueName: this.queue.queueName,
      MaxCaller: this.queue.maxCaller,
      MaxAgent: this.queue.maxAgent,
      CallerTimeout: this.queue.callerTimeout,
      AgentTimeout: this.queue.agentTimeout,
      RingStrategy: this.queue.ringStrategy,
      WrapupTime: this.queue.wrapupTime,
      MemberDelay: this.queue.memberDelay,
      AnnounceFrequency: this.queue.announceFrequency,
      Username: this.queue.username,
      Password: this.queue.password,
      SmartQueue: willBeEnhanced
    };
    this.queueService.advQueueEdit(params).then(result => {
      if (result.status === "success") {
        this.queueService.advQueueBrowse().then(res => {
          if (res.status === "success") {
            this.setQueueReporting();
            this.setCallRecording();
            this.getEmailReportsText();
            this.showEnhancedFields = willBeEnhanced;
          } else {
            this.showErrorSnackbar(res.data.message);
            this.queueService.clearErrors();
          }
        });
      } else {
        this.showErrorSnackbar(result.data.message);
        this.queueService.clearErrors();
      }
    });
  }

  showErrorSnackbar(message: string): void {
    this.snackbar.openSnackBar(message, "error");
  }

  handleEnhancedSuccessError(event: MatSlideToggleChange): void {
    this.unsubscriber.add(
      this.queueService.state
        .pipe(
          skip(1),
          filter(({ loading }) => !loading),
          take(1)
        )
        .subscribe(state => {
          if (state.errors.length === 0) {
            this.snackbar.openSnackBar(
              "Enhanced queues are " + (event.checked ? "enabled." : "disabled."),
              "success"
            );
          } else {
            this.snackbar.openSnackBar(
              state.errors[0]?.message ||
                this.translate.instant("ONSIP_I18N.THERE_WAS_AN_ERROR_SAVING_YOUR_INFORMATION"),
              "error"
            );
            this.queueService.clearErrors();
          }
        })
    );
  }

  togglePanel(): void {
    this.editPanel.emit();
  }
}
